import { type VariantProps, cva } from "class-variance-authority"
import { ReactNode } from "react"

import { cn } from "@/lib/utils"
import { Icon, IconInfoCircle, IconProps } from "@tabler/icons-react"

import { Tooltip } from "./Tooltip"
import { PolymorphicComponentProps } from "./types"

const tagVariants = cva(
  cn(
    "flex h-fit w-fit items-center justify-center gap-1 rounded-full px-3 py-1 transition-colors",
  ),
  {
    variants: {
      variant: {
        primary: "",
        secondary: "bg-brand-secondary-100 text-brand-secondary-700",
        "accent-1": "bg-accent-1-200 text-accent-1-700",
        "accent-2": "bg-accent-2-100 text-accent-2-800",
        "accent-3": "bg-accent-3-100 text-accent-3-800",
        success: "bg-success-100 text-success-900",
        error: "bg-error-100 text-error-600",
        neutral: "bg-sand-200 text-grey-700",
      },
      size: {
        small: "text-body2",
        medium: "text-body1",
      },
      background: {
        light: "",
        dark: "",
      },
      active: {
        true: "",
        false: "",
      },
      activeOnGroupHover: {
        true: "",
        false: "",
      },
      activeOnGroupAriaSelected: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      // Primary variant with background and active states
      {
        variant: "primary",
        background: "light",
        active: false,
        className: "bg-brand-primary-100 text-brand-primary-700",
      },
      {
        variant: "primary",
        background: "light",
        active: true,
        className: "bg-brand-primary-200 text-brand-primary-700",
      },
      {
        variant: "primary",
        background: "dark",
        active: false,
        className: "bg-brand-primary-500 text-brand-primary-100",
      },
      {
        variant: "primary",
        background: "dark",
        active: true,
        className: "bg-brand-primary-600 text-brand-primary-100",
      },
      // Active states for other variants
      {
        variant: "secondary",
        active: true,
        className: "bg-brand-secondary-200 text-brand-secondary-900",
      },
      {
        variant: "accent-1",
        active: true,
        className: "bg-accent-1-200 text-accent-1-800",
      },
      {
        variant: "accent-2",
        active: true,
        className: "bg-accent-2-200 text-accent-2-900",
      },
      {
        variant: "accent-3",
        active: true,
        className: "bg-accent-3-200 text-accent-3-900",
      },
      {
        variant: "success",
        active: true,
        className: "bg-success-200 text-success-900",
      },
      {
        variant: "error",
        active: true,
        className: "bg-error-200 text-error-800",
      },
      {
        variant: "neutral",
        active: true,
        className: "bg-sand-300 text-grey-800",
      },
      // Group hover states
      {
        variant: "primary",
        activeOnGroupHover: true,
        className:
          "group-hover:bg-brand-primary-200 group-hover:text-brand-primary-600",
      },
      {
        variant: "secondary",
        activeOnGroupHover: true,
        className:
          "group-hover:bg-brand-secondary-200 group-hover:text-brand-secondary-900",
      },
      {
        variant: "accent-1",
        activeOnGroupHover: true,
        className: "group-hover:bg-accent-1-200 group-hover:text-accent-1-800",
      },
      {
        variant: "accent-2",
        activeOnGroupHover: true,
        className: "group-hover:bg-accent-2-200 group-hover:text-accent-2-900",
      },
      {
        variant: "accent-3",
        activeOnGroupHover: true,
        className: "group-hover:bg-accent-3-200 group-hover:text-accent-3-900",
      },
      {
        variant: "success",
        activeOnGroupHover: true,
        className: "group-hover:bg-success-200 group-hover:text-success-900",
      },
      {
        variant: "error",
        activeOnGroupHover: true,
        className: "group-hover:bg-error-200 group-hover:text-error-800",
      },
      {
        variant: "neutral",
        activeOnGroupHover: true,
        className: "group-hover:bg-sand-300 group-hover:text-grey-800",
      },
      // Group aria-selected states
      {
        variant: "primary",
        activeOnGroupAriaSelected: true,
        className:
          "group-aria-selected:bg-brand-primary-200 group-aria-selected:text-brand-primary-600",
      },
      {
        variant: "secondary",
        activeOnGroupAriaSelected: true,
        className:
          "group-aria-selected:bg-brand-secondary-200 group-aria-selected:text-brand-secondary-900",
      },
      {
        variant: "accent-1",
        activeOnGroupAriaSelected: true,
        className:
          "group-aria-selected:bg-accent-1-200 group-aria-selected:text-accent-1-800",
      },
      {
        variant: "accent-2",
        activeOnGroupAriaSelected: true,
        className:
          "group-aria-selected:bg-accent-2-200 group-aria-selected:text-accent-2-900",
      },
      {
        variant: "accent-3",
        activeOnGroupAriaSelected: true,
        className:
          "group-aria-selected:bg-accent-3-200 group-aria-selected:text-accent-3-900",
      },
      {
        variant: "success",
        activeOnGroupAriaSelected: true,
        className:
          "group-aria-selected:bg-success-200 group-aria-selected:text-success-900",
      },
      {
        variant: "error",
        activeOnGroupAriaSelected: true,
        className:
          "group-aria-selected:bg-error-200 group-aria-selected:text-error-800",
      },
      {
        variant: "neutral",
        activeOnGroupAriaSelected: true,
        className: "group-aria-selected:bg-sand-300 group-aria-selected:text-grey-800",
      },
    ],
    defaultVariants: {
      variant: "neutral",
      size: "small",
      background: "light",
      active: false,
      activeOnGroupHover: false,
      activeOnGroupAriaSelected: false,
    },
  },
)

export type TagProps = Omit<
  PolymorphicComponentProps<
    "span",
    {
      className?: string
      label: ReactNode
      icon?: Icon
      iconPosition?: "left" | "right"
      iconStroke?: IconProps["stroke"]
      iconClassName?: string
      tooltipMsg?: string
      tooltipDark?: boolean
    } & VariantProps<typeof tagVariants>
  >,
  "color" | "children"
>

export function Tag({
  className = "",
  label,
  icon,
  iconPosition = "left",
  iconStroke = 3,
  iconClassName,
  size = "small",
  background = "light",
  variant = "neutral",
  active = false,
  activeOnGroupHover = false,
  activeOnGroupAriaSelected = false,
  tooltipMsg,
  tooltipDark,
  ...props
}: TagProps) {
  const IconComponent = icon

  const iconSize = size === "small" ? 12 : size === "medium" ? 14 : 16

  return (
    <span
      className={cn(
        tagVariants({
          variant,
          size,
          background,
          active,
          activeOnGroupHover,
          activeOnGroupAriaSelected,
          className,
        }),
      )}
      {...props}
    >
      {iconPosition === "left" && IconComponent ? (
        <IconComponent className={iconClassName} stroke={iconStroke} size={iconSize} />
      ) : null}
      <span className="overflow-hidden">{label}</span>
      {iconPosition === "right" && IconComponent ? (
        <IconComponent className={iconClassName} stroke={iconStroke} size={iconSize} />
      ) : null}
      {tooltipMsg && (
        <Tooltip content={tooltipMsg} className="max-w-xs" dark={tooltipDark}>
          <IconInfoCircle
            className={cn(iconClassName, "ml-0.5 print:hidden")}
            stroke={2}
            size={iconSize}
          />
        </Tooltip>
      )}
    </span>
  )
}

export { tagVariants }
